import {
  Box,
  Button,
  FormControl,
  FormLabel,
  TextField,
  Grid2 as Grid,
  Typography,
  Divider,
  FormControlLabel,
  Switch,
  MenuItem,
  Select,
  Chip,
  Stack,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import { FormProvider } from "react-hook-form";
import { Save as SaveIcon } from "@mui/icons-material";
import { CreateChatContentDto } from "@/dto/ChatContentDto";
import { Yn } from "@/constants/code";
import useCreateForm from "./useCreateForm";

/**
 * 채팅 컨텐츠 생성 폼 Props
 */
export interface CreateFormProps {
  /**
   * 폼 제출 핸들러
   */
  onSubmit: (data: CreateChatContentDto) => void;

  /**
   * 로딩 상태
   */
  isLoading?: boolean;
}

/**
 * 채팅 컨텐츠 생성 폼 컴포넌트
 * @description QNA 채팅 컨텐츠를 생성하는 폼입니다.
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-16
 */
export const CreateForm = (props: CreateFormProps) => {
  const { onSubmit, isLoading = false } = props;
  const { methods, handleSubmit, categoryOptions, actionTypeOptions } =
    useCreateForm(onSubmit);

  const defaultProps = {
    size: "small" as const,
    variant: "outlined" as const,
    fullWidth: true,
  };

  return (
    <FormProvider {...methods}>
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backdropFilter: "blur(3px)",
        }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box display="flex" flexDirection="column" gap={4}>
        <Box>
          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{ p: 3 }}
          >
            <Grid container spacing={3}>
              {/* 1. 기본 정보 */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                  기본 정보
                </Typography>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <FormLabel required>카테고리</FormLabel>
                  <Select
                    {...methods.register("category")}
                    {...defaultProps}
                    value={methods.watch("category") || ""}
                    onChange={(e) =>
                      methods.setValue("category", e.target.value)
                    }
                    error={!!methods.formState.errors.category}
                  >
                    {categoryOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {methods.formState.errors.category && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5 }}
                    >
                      {methods.formState.errors.category.message}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <FormLabel>키워드</FormLabel>
                  <TextField
                    {...methods.register("keywords")}
                    {...defaultProps}
                    placeholder="쉼표로 구분하여 입력 (예: 수업, 등록, 문의)"
                    helperText="검색에 사용될 키워드를 입력하세요"
                  />
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth>
                  <FormLabel required>컨텐츠 내용</FormLabel>
                  <TextField
                    multiline
                    rows={6}
                    {...methods.register("content")}
                    {...defaultProps}
                    placeholder="사용자에게 표시될 답변 내용을 입력하세요"
                    error={!!methods.formState.errors.content}
                    helperText={
                      methods.formState.errors.content?.message ||
                      "Markdown 형식을 지원합니다"
                    }
                  />
                </FormControl>
              </Grid>

              {/* 2. 액션 설정 */}
              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                  액션 설정 (선택사항)
                </Typography>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <FormLabel>액션 타입</FormLabel>
                  <Select
                    {...methods.register("actionType")}
                    {...defaultProps}
                    value={methods.watch("actionType") || ""}
                    onChange={(e) =>
                      methods.setValue("actionType", e.target.value)
                    }
                  >
                    <MenuItem value="">선택 안함</MenuItem>
                    {actionTypeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <FormLabel>액션 텍스트</FormLabel>
                  <TextField
                    {...methods.register("actionText")}
                    {...defaultProps}
                    placeholder="버튼에 표시될 텍스트 (예: 자세히 보기)"
                  />
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth>
                  <FormLabel>액션 URL</FormLabel>
                  <TextField
                    {...methods.register("actionUrl")}
                    {...defaultProps}
                    placeholder="https://example.com/page"
                    type="url"
                  />
                </FormControl>
              </Grid>

              {/* 3. 표시 설정 */}
              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                  표시 설정
                </Typography>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", height: "100%" }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={methods.watch("useYn") === Yn.Y}
                        onChange={(e) =>
                          methods.setValue(
                            "useYn",
                            e.target.checked ? Yn.Y : Yn.N
                          )
                        }
                      />
                    }
                    label="사용 활성화"
                  />
                </Box>
              </Grid>

              {/* 키워드 미리보기 */}
              {methods.watch("keywords") && (
                <Grid size={{ xs: 12 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    키워드 미리보기:
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {methods
                      .watch("keywords")
                      ?.split(",")
                      .map((keyword, index) => (
                        <Chip
                          key={index}
                          label={keyword.trim()}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                  </Stack>
                </Grid>
              )}
            </Grid>

            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              mt={4}
              sx={{ borderTop: 1, borderColor: "divider", pt: 3, pb: 3 }}
            >
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={isLoading}
                startIcon={<SaveIcon />}
                sx={{ minWidth: 200 }}
              >
                {isLoading ? "저장 중..." : "컨텐츠 생성"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </FormProvider>
  );
};
